import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, User, ArrowRight, BookOpen, Clock, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEOHead } from "@/components/SEO";
import { CollectionPageSchema } from "@/components/Schema/CollectionPageSchema";
import { blogService, type BlogMeta } from "@/services/blogService";
import emailjs from '@emailjs/browser';
import { toast } from "sonner";

const Blog = () => {
  const [posts, setPosts] = useState<BlogMeta[]>([]);
  const [displayedPosts, setDisplayedPosts] = useState<BlogMeta[]>([]);
  const [featuredPost, setFeaturedPost] = useState<BlogMeta | null>(null);
  const [categories, setCategories] = useState<string[]>(['All']);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [postsPerPage] = useState(6);
  const [currentPage, setCurrentPage] = useState(1);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    loadPosts();
  }, []);

  useEffect(() => {
    filterAndSearchPosts();
  }, [selectedCategory, searchQuery, posts, currentPage]);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const allPosts = await blogService.getAllPosts();
      setPosts(allPosts);
      
      const featured = await blogService.getFeaturedPost();
      setFeaturedPost(featured);
      
      const cats = blogService.getCategories();
      setCategories(cats);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSearchPosts = async () => {
    let filtered = posts;

    if (selectedCategory !== 'All') {
      filtered = posts.filter(post => post.category === selectedCategory);
    }

    if (searchQuery) {
      const searchResults = await blogService.searchPosts(searchQuery);
      filtered = filtered.filter(post => 
        searchResults.some(result => result.slug === post.slug)
      );
    }

    const endIndex = currentPage * postsPerPage;
    setDisplayedPosts(filtered.slice(0, endIndex));
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const loadMorePosts = () => {
    setCurrentPage(prev => prev + 1);
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setSubscribing(true);
    try {
      const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
      const templateId = import.meta.env.VITE_EMAILJS_NEWSLETTER_TEMPLATE_ID || import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
      const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

      if (!serviceId || !templateId || !publicKey) {
        toast.error("Email service not configured. Please try again later.");
        return;
      }

      await emailjs.send(
        serviceId,
        templateId,
        {
          to_email: email,
          user_email: email,
          message: `New newsletter subscription from: ${email}`,
        },
        publicKey
      );

      toast.success("Successfully subscribed to our newsletter!");
      setEmail('');
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast.error("Failed to subscribe. Please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  const hasMorePosts = displayedPosts.length < posts.filter(post => 
    (selectedCategory === 'All' || post.category === selectedCategory) &&
    (!searchQuery || posts.some(p => p.slug === post.slug))
  ).length;

  return (
    <>
      <SEOHead
        title="Technology Blog | AI, NetSuite, Shopify Plus Insights & Tutorials"
        description="Stay ahead with expert insights, industry trends, and practical guides from FortifyTek's technology specialists. AI solutions, NetSuite development, Shopify Plus, and enterprise software tutorials."
        keywords={[
          'technology blog',
          'AI insights',
          'NetSuite tutorials',
          'Shopify Plus guides',
          'enterprise software',
          'machine learning articles',
          'ERP development',
          'e-commerce optimization',
          'business intelligence',
          'technology trends'
        ]}
        canonicalUrl="https://www.fortifytek.com/blog"
        schemaType="WebPage"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.fortifytek.com/' },
          { name: 'Blog', url: 'https://www.fortifytek.com/blog' }
        ]}
      />
      <CollectionPageSchema
        name="Fortify Technology Blog"
        description="Expert insights, industry trends, and practical guides on AI, NetSuite, and Shopify Plus."
        url="https://www.fortifytek.com/blog"
      />
      <div className="min-h-screen bg-background">

      <Navigation />
      
      <section className="pt-40 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-primary/20 rounded-full blur-[120px] opacity-30"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-8 leading-tight animate-fade-in">
              <span className="block mb-2">
                Insights & Innovation
              </span>
              <span className="block text-amber-400 pb-2">
                Technology Blog
              </span>
            </h1>
            
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
              Expert insights, tutorials, and industry trends to keep you ahead of the curve.
            </p>

            <form onSubmit={handleSearch} className="flex gap-4 max-w-xl mx-auto mb-8">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 text-white placeholder:text-white/50 backdrop-blur-xl rounded-full px-12 py-3 focus:outline-none focus:border-blue-400"
                />
              </div>
              <Button 
                type="submit"
                className="bg-white text-black hover:bg-zinc-200 rounded-full"
              >
                Search
              </Button>
            </form>
          </div>
        </div>
      </section>

      {loading ? (
        <div className="container mx-auto px-6 py-16 text-center">
          <div className="animate-pulse text-white text-xl">Loading blog posts...</div>
        </div>
      ) : (
        <>
          {featuredPost && (
            <section className="py-16 relative overflow-hidden">
              <div className="container mx-auto px-6 relative z-10">
                <Link to={`/blog/${featuredPost.slug}`}>
                  <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden hover:border-amber-500/50 transition-all duration-500 hover:scale-105 group animate-fade-in">
                    <div className="grid grid-cols-1 lg:grid-cols-2">
                      <div className="aspect-video lg:aspect-square relative overflow-hidden">
                        <img 
                          src={featuredPost.image} 
                          alt={featuredPost.title}
                          className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-all duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-blue-600/90 text-white rounded-full text-sm font-medium">
                            Featured
                          </span>
                        </div>
                      </div>
                      
                      <div className="p-8 lg:p-12 flex flex-col justify-center">
                        <div className="mb-4">
                          <span className="px-3 py-1 bg-primary/30 text-primary rounded-full text-sm font-medium border border-primary/20">
                            {featuredPost.category}
                          </span>
                        </div>
                        
                        <h2 className="text-3xl font-bold text-white mb-4 group-hover:text-amber-400 transition-colors">
                          {featuredPost.title}
                        </h2>
                        
                        <p className="text-white/70 mb-6 leading-relaxed text-lg">
                          {featuredPost.excerpt}
                        </p>
                        
                        <div className="flex items-center gap-6 mb-8 text-white/60">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            <span>{featuredPost.author}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>{new Date(featuredPost.date).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span>{featuredPost.readTime}</span>
                          </div>
                        </div>
                        
                        <Button 
                          className="bg-white text-black hover:bg-zinc-200 font-medium transition-all duration-300 hover:scale-105 group w-fit"
                        >
                          Read Full Article
                          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            </section>
          )}

          <section className="py-24 relative overflow-hidden">
            <div className="absolute inset-0 -z-10 overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/10 rounded-full blur-[120px] opacity-20"></div>
            </div>
            
            <div className="container mx-auto px-6 relative z-10">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold mb-6">
                  <span className="text-white">
                    Latest Articles
                  </span>
                </h2>
                <p className="text-xl text-white/70 max-w-3xl mx-auto">
                  Expert insights and practical guides to help you stay ahead in the rapidly evolving tech landscape.
                </p>
              </div>

              <div className="flex flex-wrap justify-center gap-4 mb-12">
                {categories.map((category) => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? "default" : "outline"}
                    className={selectedCategory === category 
                      ? "bg-white text-black hover:bg-zinc-200" 
                      : "border-zinc-700 text-white hover:bg-zinc-800"
                    }
                    onClick={() => handleCategoryChange(category)}
                  >
                    {category}
                  </Button>
                ))}
              </div>

              {displayedPosts.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-white/70 text-xl">No blog posts found. Check back soon!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {displayedPosts.map((post, index) => (
                    <Link to={`/blog/${post.slug}`} key={post.slug}>
                      <article
                        className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden hover:border-amber-500/50 transition-all duration-500 hover:scale-105 group animate-fade-in h-full"
                        style={{ animationDelay: `${index * 150}ms` }}
                      >
                        <div className="aspect-video relative overflow-hidden">
                          <img 
                            src={post.image} 
                            alt={post.title}
                            className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-all duration-500 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                          <div className="absolute top-4 left-4">
                            <span className="px-3 py-1 bg-primary/90 text-white rounded-full text-xs font-medium">
                              {post.category}
                            </span>
                          </div>
                        </div>
                        
                        <div className="p-6">
                          <h3 className="text-xl font-bold text-white mb-3 group-hover:text-amber-400 transition-colors line-clamp-2">
                            {post.title}
                          </h3>
                          
                          <p className="text-white/70 mb-4 leading-relaxed line-clamp-3">
                            {post.excerpt}
                          </p>
                          
                          <div className="flex items-center justify-between text-white/60 text-sm mb-4">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4" />
                              <span>{post.author}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              <span>{post.readTime}</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-white/60 text-sm">
                              <Calendar className="w-4 h-4" />
                              <span>{new Date(post.date).toLocaleDateString()}</span>
                            </div>
                            
                            <Button 
                              variant="ghost"
                              size="sm"
                              className="text-amber-400 hover:text-white hover:bg-amber-500/20 group"
                            >
                              Read More
                              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </Button>
                          </div>
                        </div>
                      </article>
                    </Link>
                  ))}
                </div>
              )}

              {hasMorePosts && (
                <div className="text-center mt-16">
                  <Button 
                    size="lg"
                    className="bg-white text-black hover:bg-zinc-200 px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:scale-105"
                    onClick={loadMorePosts}
                  >
                    Load More Articles
                  </Button>
                </div>
              )}
            </div>
          </section>
        </>
      )}

      <section className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-12 text-center max-w-4xl mx-auto">
            <BookOpen className="w-16 h-16 text-amber-500 mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-white">
                Stay Updated
              </span>
            </h2>
            <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
              Get the latest insights, tutorials, and industry trends delivered straight to your inbox. 
              Join thousands of tech professionals who read our newsletter.
            </p>
            
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-lg mx-auto">
              <input 
                type="email" 
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={subscribing}
                className="flex-1 w-full sm:w-auto bg-white/10 border border-white/20 text-white placeholder:text-white/50 backdrop-blur-xl rounded-full px-6 py-3 focus:outline-none focus:border-blue-400"
              />
              <Button 
                type="submit"
                disabled={subscribing}
                className="bg-white text-black hover:bg-zinc-200 px-8 py-3 rounded-full font-medium transition-all duration-300 hover:scale-105 disabled:bg-zinc-700 disabled:text-zinc-400"
              >
                {subscribing ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
            
            <p className="text-white/50 text-sm mt-4">
              No spam, unsubscribe at any time. We respect your privacy.
            </p>
          </div>
        </div>
      </section>

        <Footer />
      </div>
    </>
  );
};

export default Blog;